import { Injectable, PLATFORM_ID, Signal, signal, OnDestroy, inject } from '@angular/core';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';

import { fromEvent, debounceTime, map } from 'rxjs';

import { noop } from '@fcom/core/utils/utils';
import { WindowRef } from '@fcom/core/providers/window';

import { Breakpoint } from '../../interfaces/breakpoint.interface';

type BreakpointList = Partial<Record<Breakpoint, string>>;

const BREAKPOINT_QUERIES: BreakpointList = Object.freeze({
  mobile: 'only screen and (max-width: 599px)',
  tablet: 'only screen and (min-width: 600px) and (max-width: 1023px)',
  tablet_down: 'only screen and (max-width: 1023px)',
  laptop_up: 'only screen and (min-width: 1024px)',
  small_desktop: 'only screen and (min-width: 1024px) and (max-width: 1599px)',
  large_desktop: 'only screen and (min-width: 1600px)',
});

const EMPTY_MQL: MediaQueryList = {
  matches: false,
  media: '',
  onchange: null,
  addEventListener: noop,
  removeEventListener: noop,
  addListener: noop,
  removeListener: noop,
  dispatchEvent: () => false,
};

export type BreakpointName = keyof typeof BREAKPOINT_QUERIES;

export interface ViewportSize {
  width: number;
  height: number;
}

const EMPTY_VIEWPORT_SIZE: ViewportSize = Object.freeze({ width: 0, height: 0 });

const isSameViewportSize = (a: ViewportSize, b: ViewportSize): boolean => {
  return a.width === b.width && a.height === b.height;
};

type BreakPointRegistry = Partial<Record<BreakpointName, BreakpointMatcher>>;
type BreakpointMatcher = {
  matcher: MediaQueryList;
  signal: Signal<boolean>;
  listener: (event: MediaQueryListEvent) => void;
};

const ssrBreakpointMatcher: BreakpointMatcher = {
  matcher: EMPTY_MQL,
  signal: signal(false).asReadonly(),
  listener: noop,
};

@Injectable({
  providedIn: 'root',
})
export class MediaQueryService implements OnDestroy {
  private windowRef = inject(WindowRef);
  private platform = inject(PLATFORM_ID);

  private breakpointRegistry: BreakPointRegistry;
  private readonly fallbackBreakpointSignal: Signal<boolean> = signal(false).asReadonly();

  readonly viewportSize: Signal<ViewportSize>;

  constructor() {
    if (isPlatformBrowser(this.platform)) {
      this.viewportSize = toSignal(
        fromEvent<Event>(this.windowRef.nativeWindow, 'resize').pipe(
          debounceTime(100),
          map(() => {
            return this.readViewportSize();
          })
        ),
        { initialValue: this.readViewportSize(), equal: isSameViewportSize }
      );
    } else {
      this.viewportSize = signal(EMPTY_VIEWPORT_SIZE).asReadonly();
    }

    this.breakpointRegistry = this.createBreakpointRegistry(BREAKPOINT_QUERIES, isPlatformServer(this.platform));
  }

  ngOnDestroy(): void {
    if (isPlatformBrowser(this.platform)) {
      this.clearBreakpointListeners();
    }
  }

  /**
   * Returns a Signal that indicates whether the specified breakpoint matches the current viewport.
   * @param breakpoint The predefied media query breakpoint to check.
   */
  isBreakpoint(breakpoint: BreakpointName): Signal<boolean> {
    return this.breakpointRegistry[breakpoint]?.signal ?? this.fallbackBreakpointSignal;
  }

  private readViewportSize(): ViewportSize {
    const { innerWidth, innerHeight } = this.windowRef.nativeWindow;
    return { width: innerWidth, height: innerHeight };
  }

  private registerBreakpointMatcher(query: string): BreakpointMatcher {
    const mediaQueryList = this.windowRef.nativeWindow.matchMedia(query);
    const breakPointSignal = signal(mediaQueryList.matches);

    const listener = (event: MediaQueryListEvent) => breakPointSignal.set(event.matches);

    mediaQueryList.addEventListener('change', listener);

    return { matcher: mediaQueryList, signal: breakPointSignal.asReadonly(), listener };
  }

  private createBreakpointRegistry(breakpoints: BreakpointList, isServer = false): BreakPointRegistry {
    return Object.keys(breakpoints).reduce<BreakPointRegistry>((registry, breakpoint) => {
      registry[breakpoint] = isServer ? ssrBreakpointMatcher : this.registerBreakpointMatcher(breakpoints[breakpoint]);
      return registry;
    }, {});
  }

  private clearBreakpointListeners(): void {
    Object.values(this.breakpointRegistry).forEach(({ matcher, listener }) =>
      matcher.removeEventListener('change', listener)
    );
    this.breakpointRegistry = {};
  }
}
